import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { QrCode, User, GraduationCap, BookOpen, Lock, Eye, EyeOff, MapPin, School, ChevronLeft, ChevronRight, Users, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = ({ onLogin }) => {
  const navigate = useNavigate();
  
  // Student form states
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  
  // Multi-step registration states
  const [registrationStep, setRegistrationStep] = useState(1); // 1: City, 2: School, 3: Class, 4: Identity
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [newClassName, setNewClassName] = useState("");
  const [showNewClassInput, setShowNewClassInput] = useState(false);
  const [classType, setClassType] = useState("terminale");
  
  // Data states
  const [cities, setCities] = useState([]);
  const [demoCities, setDemoCities] = useState([]);
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classTypes, setClassTypes] = useState({});
  const [similarClasses, setSimilarClasses] = useState([]);
  
  // Auth flow states
  const [authStep, setAuthStep] = useState("identify"); // "identify", "create_password", "login"
  const [pendingStudent, setPendingStudent] = useState(null);

  // Load cities on mount
  useEffect(() => {
    fetchCities();
  }, []);

  // Load schools when city changes
  useEffect(() => {
    if (selectedCity) {
      fetchSchools(selectedCity);
    }
  }, [selectedCity]);

  // Load classes when school changes
  useEffect(() => {
    if (selectedSchool) {
      fetchClasses(selectedSchool.id);
    }
  }, [selectedSchool]);

  const fetchCities = async () => {
    try {
      const response = await axios.get(`${API}/etablissements/villes`);
      setCities(response.data.cities || []);
      setDemoCities(response.data.demo_cities || ["Démo"]);
    } catch (error) {
      console.error("Error fetching cities:", error);
      toast.error("Erreur lors du chargement des villes");
    }
  };

  const fetchSchools = async (city) => {
    try {
      const response = await axios.get(`${API}/etablissements/by-city/${encodeURIComponent(city)}`);
      setSchools(response.data.etablissements || []);
    } catch (error) {
      console.error("Error fetching schools:", error);
      toast.error("Erreur lors du chargement des établissements");
    }
  };

  const fetchClasses = async (schoolId) => {
    try {
      const response = await axios.get(`${API}/etablissements/${schoolId}/classes`);
      setClasses(response.data.classes || []);
      setClassTypes(response.data.class_types || {});
    } catch (error) {
      console.error("Error fetching classes:", error);
      toast.error("Erreur lors du chargement des classes");
    }
  };

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setSelectedSchool(null);
    setSelectedClass(null);
    setRegistrationStep(2);
  };

  const handleSchoolSelect = (school) => {
    setSelectedSchool(school);
    setSelectedClass(null);
    setRegistrationStep(3);
  };

  const handleClassSelect = (cls) => {
    setSelectedClass(cls);
    setSimilarClasses([]);
    setShowNewClassInput(false);
    setRegistrationStep(4);
  };

  const handleCreateClass = async () => {
    if (!newClassName.trim()) {
      toast.error("Veuillez entrer un nom de classe");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/etablissements/${selectedSchool.id}/classes?class_name=${encodeURIComponent(newClassName.trim())}&class_type=${classType}`
      );
      
      if (response.data.status === "similar_found") {
        setSimilarClasses(response.data.suggestions);
        toast.info("Des classes similaires existent déjà");
      } else {
        const newClass = response.data.class;
        setSelectedClass(newClass);
        setShowNewClassInput(false);
        setSimilarClasses([]);
        setRegistrationStep(4);
        toast.success(`Classe "${newClass.name}" créée !`);
      }
    } catch (error) {
      console.error("Error creating class:", error);
      toast.error("Erreur lors de la création de la classe");
    } finally {
      setLoading(false);
    }
  };

  const handleForceCreateClass = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/etablissements/${selectedSchool.id}/classes/force?class_name=${encodeURIComponent(newClassName.trim())}&class_type=${classType}`
      );
      const newClass = response.data.class;
      setSelectedClass(newClass);
      setShowNewClassInput(false);
      setSimilarClasses([]);
      setRegistrationStep(4);
      toast.success(`Classe "${newClass.name}" créée !`);
    } catch (error) {
      console.error("Error force creating class:", error);
      toast.error("Erreur lors de la création de la classe");
    } finally {
      setLoading(false);
    }
  };

  const handleIdentify = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("Veuillez remplir votre prénom et nom");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/students/register`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        class_name: selectedClass.name,
        etablissement_id: selectedSchool.id
      });
      
      const { status, student, password_required } = response.data;
      setPendingStudent(student);
      
      if (status === "created" || !password_required) {
        setAuthStep("create_password");
        toast.info("Bienvenue ! Créez votre mot de passe pour sécuriser votre compte.");
      } else {
        setAuthStep("login");
        toast.info("Entrez votre mot de passe pour vous connecter.");
      }
    } catch (error) {
      console.error("Identify error:", error);
      toast.error("Erreur de connexion. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePassword = async (e) => {
    e.preventDefault();
    
    if (password.length < 4) {
      toast.error("Le mot de passe doit contenir au moins 4 caractères");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/students/set-password`, {
        student_id: pendingStudent.id,
        password: password
      });
      
      toast.success(`Bienvenue ${response.data.first_name} !`);
      onLogin({
        ...response.data,
        etablissement_id: selectedSchool.id,
        etablissement_name: selectedSchool.name
      }, "student");
    } catch (error) {
      console.error("Set password error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création du mot de passe");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!password) {
      toast.error("Veuillez entrer votre mot de passe");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/students/login`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        class_name: selectedClass.name,
        password: password
      });
      
      toast.success(`Bienvenue ${response.data.first_name} !`);
      onLogin({
        ...response.data,
        etablissement_id: selectedSchool.id,
        etablissement_name: selectedSchool.name
      }, "student");
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    if (authStep !== "identify") {
      setAuthStep("identify");
      setPassword("");
      setConfirmPassword("");
      setPendingStudent(null);
    } else if (registrationStep > 1) {
      setRegistrationStep(registrationStep - 1);
      if (registrationStep === 2) {
        setSelectedCity("");
        setSchools([]);
      } else if (registrationStep === 3) {
        setSelectedSchool(null);
        setClasses([]);
      } else if (registrationStep === 4) {
        setSelectedClass(null);
        setShowNewClassInput(false);
        setSimilarClasses([]);
      }
    }
  };

  const resetAll = () => {
    setRegistrationStep(1);
    setSelectedCity("");
    setSelectedSchool(null);
    setSelectedClass(null);
    setSchools([]);
    setClasses([]);
    setFirstName("");
    setLastName("");
    setPassword("");
    setConfirmPassword("");
    setAuthStep("identify");
    setPendingStudent(null);
    setShowNewClassInput(false);
    setSimilarClasses([]);
    setNewClassName("");
  };

  const simulateQRScan = () => {
    setShowQRScanner(true);
    setTimeout(() => {
      setShowQRScanner(false);
      toast.info("QR Code scanné ! Veuillez confirmer vos informations.");
    }, 2000);
  };

  // Render progress indicator
  const renderProgress = () => {
    const steps = [
      { num: 1, label: "Ville", icon: MapPin },
      { num: 2, label: "Établissement", icon: School },
      { num: 3, label: "Classe", icon: Users },
      { num: 4, label: "Identité", icon: User }
    ];

    return (
      <div className="flex items-center justify-between mb-6 px-2">
        {steps.map((step, idx) => {
          const Icon = step.icon;
          const isActive = registrationStep === step.num;
          const isCompleted = registrationStep > step.num;
          
          return (
            <div key={step.num} className="flex items-center">
              <div className={`flex flex-col items-center ${idx > 0 ? 'ml-2' : ''}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  isCompleted ? 'bg-green-500 text-white' :
                  isActive ? 'bg-[#1A365D] text-white' :
                  'bg-gray-200 text-gray-500'
                }`}>
                  {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className={`text-xs mt-1 ${isActive ? 'text-[#1A365D] font-medium' : 'text-gray-500'}`}>
                  {step.label}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div className={`w-8 h-0.5 mx-1 mt-[-16px] ${
                  registrationStep > step.num ? 'bg-green-500' : 'bg-gray-200'
                }`} />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Step 1: City Selection
  const renderCitySelection = () => (
    <div className="space-y-4" data-testid="city-selection">
      <div className="text-center mb-4">
        <MapPin className="w-12 h-12 mx-auto text-[#1A365D] mb-2" />
        <h3 className="text-lg font-medium text-[#1A365D]">Dans quelle ville étudiez-vous ?</h3>
        <p className="text-sm text-gray-500">Sélectionnez votre ville</p>
      </div>
      
      <Select onValueChange={handleCitySelect}>
        <SelectTrigger className="w-full h-14 text-base" data-testid="city-select">
          <SelectValue placeholder="Choisir une ville..." />
        </SelectTrigger>
        <SelectContent className="max-h-64">
          {/* Demo cities first */}
          {demoCities.length > 0 && (
            <>
              <div className="px-2 py-1.5 text-xs font-semibold text-[#D4AF37] bg-[#FEF9C3]">
                🎓 Mode Démonstration
              </div>
              {demoCities.map((city) => (
                <SelectItem key={`demo-${city}`} value={city}>
                  <div className="flex items-center gap-2">
                    <span className="text-[#D4AF37]">★</span>
                    {city}
                  </div>
                </SelectItem>
              ))}
              <div className="h-px bg-gray-200 my-2" />
              <div className="px-2 py-1.5 text-xs font-semibold text-[#1A365D]">
                📍 Établissements réels
              </div>
            </>
          )}
          {cities.map((city) => (
            <SelectItem key={city} value={city}>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#1A365D]" />
                {city}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      {cities.length === 0 && (
        <p className="text-center text-gray-500 py-4">Chargement des villes...</p>
      )}
    </div>
  );

  // Step 2: School Selection
  const renderSchoolSelection = () => (
    <div className="space-y-4" data-testid="school-selection">
      <Button
        variant="ghost"
        size="sm"
        onClick={goBack}
        className="mb-2 text-gray-500 hover:text-[#1A365D]"
      >
        <ChevronLeft className="w-4 h-4 mr-1" /> Retour
      </Button>
      
      <div className="text-center mb-4">
        <School className="w-12 h-12 mx-auto text-[#1A365D] mb-2" />
        <h3 className="text-lg font-medium text-[#1A365D]">Choisissez votre établissement</h3>
        <p className="text-sm text-gray-500">{selectedCity}</p>
      </div>
      
      <Select onValueChange={(id) => handleSchoolSelect(schools.find(s => s.id === id))}>
        <SelectTrigger className="w-full h-14 text-base" data-testid="school-select">
          <SelectValue placeholder="Choisir un établissement..." />
        </SelectTrigger>
        <SelectContent className="max-h-64">
          {schools.map((school) => (
            <SelectItem key={school.id} value={school.id}>
              <div className="flex flex-col items-start">
                <span className="font-medium">{school.name}</span>
                <span className="text-xs text-gray-500">{school.postal_code} - {school.type === 'prepa' ? 'CPGE' : 'Lycée'}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      {schools.length === 0 && (
        <div className="text-center py-4">
          <p className="text-gray-500 mb-2">Aucun établissement trouvé à {selectedCity}</p>
          <p className="text-xs text-gray-400">Contactez votre administration pour ajouter votre établissement</p>
        </div>
      )}
    </div>
  );

  // Step 3: Class Selection
  const renderClassSelection = () => (
    <div className="space-y-4" data-testid="class-selection">
      <Button
        variant="ghost"
        size="sm"
        onClick={goBack}
        className="mb-2 text-gray-500 hover:text-[#1A365D]"
      >
        <ChevronLeft className="w-4 h-4 mr-1" /> Retour
      </Button>
      
      <div className="text-center mb-4">
        <Users className="w-12 h-12 mx-auto text-[#1A365D] mb-2" />
        <h3 className="text-lg font-medium text-[#1A365D]">Indiquez votre classe</h3>
        <p className="text-sm text-gray-500">{selectedSchool?.name}</p>
      </div>
      
      {!showNewClassInput ? (
        <>
          <div className="max-h-48 overflow-y-auto space-y-2">
            {classes.map((cls) => (
              <Button
                key={cls.id}
                variant="outline"
                className="w-full justify-start text-left h-12 hover:bg-[#1A365D] hover:text-white transition-colors"
                onClick={() => handleClassSelect(cls)}
                data-testid={`class-${cls.id}`}
              >
                <GraduationCap className="w-4 h-4 mr-3 flex-shrink-0" />
                <span>{cls.display_name || cls.name}</span>
                {cls.class_type && (
                  <span className="ml-auto text-xs opacity-75 capitalize">{cls.class_type}</span>
                )}
              </Button>
            ))}
          </div>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-500">ou</span>
            </div>
          </div>
          
          <Button
            variant="outline"
            className="w-full h-12 border-dashed border-2 text-[#1A365D] hover:bg-[#1A365D] hover:text-white"
            onClick={() => setShowNewClassInput(true)}
            data-testid="add-new-class-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter ma classe
          </Button>
        </>
      ) : (
        <div className="space-y-4">
          <div>
            <Label htmlFor="newClassName" className="text-[#0F172A]">Nom de la classe</Label>
            <Input
              id="newClassName"
              value={newClassName}
              onChange={(e) => setNewClassName(e.target.value)}
              placeholder="Ex: Terminale 3, TG2, Khâgne A/L..."
              className="mt-1 border-gray-200 focus:border-[#1A365D]"
              data-testid="new-class-name-input"
            />
          </div>
          
          <div>
            <Label htmlFor="classType" className="text-[#0F172A]">Type de classe</Label>
            <Select value={classType} onValueChange={setClassType}>
              <SelectTrigger className="mt-1 border-gray-200" data-testid="class-type-select">
                <SelectValue placeholder="Type de classe" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(classTypes).map(([key, value]) => (
                  <SelectItem key={key} value={key}>
                    {value.name} - {value.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {similarClasses.length > 0 && (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800 font-medium mb-2">Classes similaires existantes :</p>
              <div className="space-y-1">
                {similarClasses.map((similar, idx) => (
                  <Button
                    key={idx}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-amber-700 hover:bg-amber-100"
                    onClick={() => {
                      const existingClass = classes.find(c => c.name === similar.name);
                      if (existingClass) handleClassSelect(existingClass);
                    }}
                  >
                    {similar.name} ({similar.match === 'exact' ? 'identique' : 'similaire'})
                  </Button>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2 text-amber-700 border-amber-300"
                onClick={handleForceCreateClass}
                disabled={loading}
              >
                Créer quand même "{newClassName}"
              </Button>
            </div>
          )}
          
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setShowNewClassInput(false);
                setSimilarClasses([]);
                setNewClassName("");
              }}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              onClick={handleCreateClass}
              disabled={loading || !newClassName.trim()}
              className="flex-1 bg-[#1A365D] hover:bg-[#132745] text-white"
              data-testid="create-class-btn"
            >
              {loading ? "Création..." : "Créer la classe"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // Step 4: Student Identity
  const renderIdentityForm = () => {
    if (authStep === "identify") {
      return (
        <form onSubmit={handleIdentify} className="space-y-4" data-testid="identity-form">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={goBack}
            className="mb-2 text-gray-500 hover:text-[#1A365D]"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          
          <div className="text-center mb-4">
            <User className="w-12 h-12 mx-auto text-[#1A365D] mb-2" />
            <h3 className="text-lg font-medium text-[#1A365D]">Qui êtes-vous ?</h3>
            <p className="text-sm text-gray-500">
              {selectedSchool?.name} - {selectedClass?.display_name || selectedClass?.name}
            </p>
          </div>
          
          <Button 
            type="button"
            variant="outline" 
            className="w-full h-14 border-dashed border-2 border-[#1A365D] text-[#1A365D] hover:bg-[#1A365D] hover:text-white"
            onClick={simulateQRScan}
            data-testid="qr-scan-button"
          >
            <QrCode className="w-5 h-5 mr-2" />
            Scanner le QR Code de la salle
          </Button>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-500">ou remplir manuellement</span>
            </div>
          </div>
          
          <div className="space-y-3">
            <div>
              <Label htmlFor="firstName" className="text-[#0F172A]">Prénom</Label>
              <Input 
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Votre prénom"
                className="mt-1 border-gray-200 focus:border-[#1A365D]"
                data-testid="first-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="lastName" className="text-[#0F172A]">Nom</Label>
              <Input 
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Votre nom"
                className="mt-1 border-gray-200 focus:border-[#1A365D]"
                data-testid="last-name-input"
              />
            </div>
          </div>
          
          <Button 
            type="submit" 
            className="w-full h-12 bg-[#1A365D] hover:bg-[#132745] text-white"
            disabled={loading}
            data-testid="student-identify-button"
          >
            {loading ? "Vérification..." : "Continuer"}
          </Button>
        </form>
      );
    }

    if (authStep === "create_password") {
      return (
        <form onSubmit={handleCreatePassword} className="space-y-4" data-testid="create-password-form">
          <div className="text-center mb-4">
            <p className="text-[#1A365D] font-medium">
              {pendingStudent?.first_name} {pendingStudent?.last_name}
            </p>
            <p className="text-sm text-gray-500">{selectedClass?.display_name || selectedClass?.name}</p>
          </div>
          
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-600">
              <Lock className="w-4 h-4 inline mr-1" />
              Créez un mot de passe pour sécuriser votre compte. Vous l'utiliserez à chaque connexion.
            </p>
          </div>
          
          <div className="space-y-3">
            <div>
              <Label htmlFor="password" className="text-[#0F172A]">Mot de passe</Label>
              <div className="relative mt-1">
                <Input 
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 4 caractères"
                  className="pr-10 border-gray-200 focus:border-[#1A365D]"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div>
              <Label htmlFor="confirmPassword" className="text-[#0F172A]">Confirmer le mot de passe</Label>
              <Input 
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Retapez votre mot de passe"
                className="mt-1 border-gray-200 focus:border-[#1A365D]"
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          
          <Button 
            type="submit" 
            className="w-full h-12 bg-green-500 hover:bg-green-600 text-white"
            disabled={loading}
            data-testid="create-password-button"
          >
            {loading ? "Création..." : "Créer mon mot de passe"}
          </Button>
          
          <Button 
            type="button"
            variant="ghost"
            onClick={goBack}
            className="w-full text-gray-500"
          >
            Retour
          </Button>
        </form>
      );
    }

    if (authStep === "login") {
      return (
        <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
          <div className="text-center mb-4">
            <p className="text-[#1A365D] font-medium">
              {pendingStudent?.first_name} {pendingStudent?.last_name}
            </p>
            <p className="text-sm text-gray-500">{selectedClass?.display_name || selectedClass?.name}</p>
          </div>
          
          <div>
            <Label htmlFor="loginPassword" className="text-[#0F172A]">Mot de passe</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input 
                id="loginPassword"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Votre mot de passe"
                className="pl-10 pr-10 border-gray-200 focus:border-[#1A365D]"
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          
          <Button 
            type="submit" 
            className="w-full h-12 bg-[#1A365D] hover:bg-[#132745] text-white"
            disabled={loading}
            data-testid="student-login-button"
          >
            {loading ? "Connexion..." : "Se connecter"}
          </Button>
          
          <Button 
            type="button"
            variant="ghost"
            onClick={goBack}
            className="w-full text-gray-500"
          >
            Changer d'élève
          </Button>
        </form>
      );
    }
  };

  const renderStudentForm = () => {
    return (
      <div>
        {renderProgress()}
        
        {registrationStep === 1 && renderCitySelection()}
        {registrationStep === 2 && renderSchoolSelection()}
        {registrationStep === 3 && renderClassSelection()}
        {registrationStep === 4 && renderIdentityForm()}
      </div>
    );
  };

  const handleDirectorLogin = () => {
    const directorUser = {
      id: "director-001",
      first_name: "Directrice",
      last_name: "Saint-Jean Hulst",
      role: "director"
    };
    toast.success("Bienvenue Madame la Directrice !");
    onLogin(directorUser, "director");
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 hero-section"
      data-testid="login-page"
    >
      <div className="w-full max-w-md">
        <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_20bc98a4-4331-40ab-b07e-fb5b25585751/artifacts/8tur9abd_9AE940BD-27AA-4FC0-9402-213532B07955.png"
                alt="SophIA"
                className="w-24 h-24 object-contain drop-shadow-lg"
              />
            </div>
            <CardTitle className="text-3xl text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Soph<span className="text-[#00b4ff]">IA</span>
            </CardTitle>
            <CardDescription className="text-base text-gray-500">
              Intelligence Artificielle de la Sagesse
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-4">
            <Tabs defaultValue="student" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="student" className="flex items-center gap-2" data-testid="student-tab">
                  <GraduationCap className="w-4 h-4" />
                  Élève
                </TabsTrigger>
                <TabsTrigger value="director" className="flex items-center gap-2" data-testid="director-tab">
                  <User className="w-4 h-4" />
                  Direction
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="student">
                {renderStudentForm()}
              </TabsContent>
              
              <TabsContent value="director">
                <div className="space-y-4">
                  <p className="text-center text-gray-500 text-sm">
                    Accès réservé à la direction de l'établissement
                  </p>
                  <Button 
                    onClick={handleDirectorLogin}
                    className="w-full h-12 bg-[#D4AF37] hover:bg-[#B3932E] text-[#0F172A]"
                    data-testid="director-login-button"
                  >
                    Accéder au tableau de bord (démo)
                  </Button>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-gray-500">ou</span>
                    </div>
                  </div>
                  
                  <Button 
                    variant="outline"
                    onClick={() => navigate("/admin")}
                    className="w-full h-12 border-[#1A365D] text-[#1A365D] hover:bg-[#1A365D] hover:text-white"
                    data-testid="admin-login-link"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Connexion administrateur
                  </Button>
                  
                  <Button 
                    variant="ghost"
                    onClick={() => navigate("/demo")}
                    className="w-full h-10 text-[#D4AF37] hover:text-[#B3932E] hover:bg-amber-50"
                    data-testid="demo-request-link"
                  >
                    Demander un accès démo gratuit
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
            
            <Footer />
          </CardContent>
        </Card>
      </div>
      
      {showQRScanner && (
        <div className="qr-overlay" data-testid="qr-scanner-overlay">
          <div className="text-center text-white">
            <div className="w-64 h-64 border-4 border-white rounded-lg mb-4 flex items-center justify-center">
              <QrCode className="w-32 h-32 animate-pulse" />
            </div>
            <p className="text-lg">Scannez le QR Code...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
